import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { INTEREST_OPTIONS } from "@/lib/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const initial = {
  interest: INTEREST_OPTIONS[0],
  name: "",
  business: "",
  website: "",
  industry: "",
  uk_headcount: "",
  email: "",
  phone: "",
  roles_required: "",
  estimated_employees: "",
  launch_date: "",
  current_vacancies: "",
  working_hours: "",
  management_model: "",
  existing_systems: "",
  main_challenge: "",
  message: "",
};

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[15px] text-navy placeholder-slate-400 transition-all aqua-focus";

const EnquiryForm = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.business || !form.email || !form.phone) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/enquiry`, form, { timeout: 20000 });
      setSubmitted(true);
      toast.success("Thanks — enquiry received.");
      setForm(initial);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Please email hello@brighteor.uk.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="rounded-3xl border border-aqua/30 bg-aqua/5 p-10 text-center" data-testid="enquiry-success">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-aqua text-navy">
          <CheckCircle2 className="h-7 w-7" strokeWidth={2.2} />
        </div>
        <h3 className="font-display text-2xl font-semibold text-navy">Thanks — Bright EOR has received your enquiry.</h3>
        <p className="mt-3 text-slateink">We&apos;ll review your requirements and come back with next steps.</p>
        <button
          onClick={() => setSubmitted(false)}
          className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-navy hover:text-aqua transition-colors"
          data-testid="enquiry-reset"
        >
          Send another enquiry <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="enquiry-form" noValidate>
      {/* Interest */}
      <Field label="What are you interested in?">
        <RadioGroup
          value={form.interest}
          onValueChange={(v) => setForm((f) => ({ ...f, interest: v }))}
          className="grid gap-2 sm:grid-cols-2"
        >
          {INTEREST_OPTIONS.map((opt) => {
            const checked = form.interest === opt;
            const id = `interest-${opt.replace(/\s+/g, "-").toLowerCase()}`;
            return (
              <label
                key={opt}
                htmlFor={id}
                data-testid={`enquiry-radio-${id}`}
                className={`flex items-start gap-3 cursor-pointer rounded-xl border px-4 py-3 transition-all ${
                  checked ? "border-aqua bg-aqua/10 text-navy" : "border-slate-200 bg-white hover:border-aqua/50"
                }`}
              >
                <RadioGroupItem id={id} value={opt} className="mt-1 border-navy data-[state=checked]:border-aqua data-[state=checked]:text-aqua" />
                <span className="text-[14px] leading-snug">{opt}</span>
              </label>
            );
          })}
        </RadioGroup>
      </Field>

      {/* Contact */}
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Contact name" required>
          <input type="text" required className={inputCls} value={form.name} onChange={update("name")} placeholder="Jane Smith" data-testid="enquiry-input-name" />
        </Field>
        <Field label="Company name" required>
          <input type="text" required className={inputCls} value={form.business} onChange={update("business")} placeholder="Acme Ltd" data-testid="enquiry-input-business" />
        </Field>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Work email" required>
          <input type="email" required className={inputCls} value={form.email} onChange={update("email")} placeholder="jane@acme.co.uk" data-testid="enquiry-input-email" />
        </Field>
        <Field label="Phone" required>
          <input type="tel" required className={inputCls} value={form.phone} onChange={update("phone")} placeholder="+44 7…" data-testid="enquiry-input-phone" />
        </Field>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Website">
          <input type="text" className={inputCls} value={form.website} onChange={update("website")} placeholder="acme.co.uk" data-testid="enquiry-input-website" />
        </Field>
        <Field label="Industry">
          <input type="text" className={inputCls} value={form.industry} onChange={update("industry")} placeholder="e.g. Recruitment" data-testid="enquiry-input-industry" />
        </Field>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="UK employee count">
          <input type="text" className={inputCls} value={form.uk_headcount} onChange={update("uk_headcount")} placeholder="e.g. 25" data-testid="enquiry-input-headcount" />
        </Field>
        <Field label="Estimated number of employees">
          <input type="text" className={inputCls} value={form.estimated_employees} onChange={update("estimated_employees")} placeholder="e.g. 1, 3, 5+" data-testid="enquiry-input-estimated" />
        </Field>
      </div>

      <Field label="Roles required">
        <input type="text" className={inputCls} value={form.roles_required} onChange={update("roles_required")} placeholder="e.g. 2 candidate resourcers + 1 compliance admin" data-testid="enquiry-input-roles" />
      </Field>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Current vacancies">
          <input type="text" className={inputCls} value={form.current_vacancies} onChange={update("current_vacancies")} placeholder="Roles you're hiring for now" data-testid="enquiry-input-vacancies" />
        </Field>
        <Field label="Working hours">
          <input type="text" className={inputCls} value={form.working_hours} onChange={update("working_hours")} placeholder="e.g. UK 9–5" data-testid="enquiry-input-hours" />
        </Field>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Expected launch date">
          <input type="text" className={inputCls} value={form.launch_date} onChange={update("launch_date")} placeholder="e.g. Within 4–6 weeks" data-testid="enquiry-input-launch" />
        </Field>
        <Field label="Client-led or fully managed?">
          <select className={inputCls} value={form.management_model} onChange={update("management_model")} data-testid="enquiry-input-model">
            <option value="">Select…</option>
            <option value="Client-led">Client-led</option>
            <option value="Fully managed">Fully managed</option>
            <option value="Not sure yet">Not sure yet</option>
          </select>
        </Field>
      </div>

      <Field label="Existing systems">
        <input type="text" className={inputCls} value={form.existing_systems} onChange={update("existing_systems")} placeholder="e.g. Salesforce, Xero, HubSpot" data-testid="enquiry-input-systems" />
      </Field>

      <Field label="Main operational challenge">
        <textarea rows={3} className={inputCls + " resize-y"} value={form.main_challenge} onChange={update("main_challenge")} placeholder="What's the biggest capacity or operational challenge you're trying to solve?" data-testid="enquiry-input-challenge" />
      </Field>

      <Field label="Anything else?">
        <textarea rows={3} className={inputCls + " resize-y"} value={form.message} onChange={update("message")} placeholder="Optional — tell us more about what you'd like to build in India." data-testid="enquiry-input-message" />
      </Field>

      <button
        type="submit"
        disabled={submitting}
        data-testid="enquiry-submit"
        className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-navy px-6 py-4 text-[15px] font-medium text-white transition-all hover:bg-aqua hover:text-navy disabled:opacity-70"
      >
        {submitting ? (
          <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>
        ) : (
          <>Send Enquiry <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></>
        )}
      </button>

      <p className="text-xs text-slatemuted">
        By submitting you agree to Bright EOR contacting you about your enquiry. We never share your details.
      </p>
    </form>
  );
};

const Field = ({ label, required, children }) => (
  <div>
    <label className="mb-1.5 block text-[13px] font-medium tracking-tight text-slateink">
      {label}
      {required && <span className="ml-1 text-aqua">*</span>}
    </label>
    {children}
  </div>
);

export default EnquiryForm;
